/**
 * The door.
 *
 * One line of positioning, one button, and the drop target is the whole window.
 * Everything else the first build put here (privacy, non-destructiveness, what
 * the assistant does) belongs where it applies, not in front of someone who has
 * not opened a photograph yet.
 */

import { useEffect, useRef, useState } from 'react';
import {
  ACCEPTED_EXTENSIONS,
  BITMAP_EXTENSIONS,
  RAW_EXTENSIONS,
  TIFF_EXTENSIONS,
  classifySource,
} from '@lumos/core';
import { useStore } from '../state/store.ts';
import { Tip } from './controls/Tooltip.tsx';

const ACCEPT = ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`).join(',');

function upper(list: readonly string[]): string {
  return list.map((entry) => entry.toUpperCase()).join('  ');
}

const FORMATS = (
  <>
    <b>Raw</b> {upper(RAW_EXTENSIONS)}
    <br />
    <b>Also</b> {upper(TIFF_EXTENSIONS)} {upper(BITMAP_EXTENSIONS)}
  </>
);

export function Dropzone() {
  const addFiles = useStore((state) => state.addFiles);
  const notify = useStore((state) => state.notify);
  const [over, setOver] = useState(false);
  const [showFormats, setShowFormats] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Nested elements fire dragleave as the pointer crosses them; counting
    // enter/leave pairs is the only reliable way to know we have really left.
    let depth = 0;

    const take = (list: FileList | null | undefined) => {
      const files = Array.from(list ?? []);
      if (files.length === 0) return;
      const accepted = files.filter((file) => classifySource(file.name) !== null);
      const rejected = files.length - accepted.length;
      if (accepted.length > 0) addFiles(accepted);
      if (rejected > 0) {
        notify(
          accepted.length > 0
            ? `Skipped ${rejected} file${rejected === 1 ? '' : 's'} Lumos cannot read`
            : 'Lumos cannot read those files',
          'error',
        );
      }
    };

    const onDragEnter = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      depth += 1;
      setOver(true);
    };
    const onDragOver = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setOver(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      depth = 0;
      setOver(false);
      take(event.dataTransfer.files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [addFiles, notify]);

  return (
    <div className={`dropzone${over ? ' over' : ''}`}>
      <h1 className="wordmark" aria-label="Lumos">
        LUM<span>O</span>S
      </h1>

      <p>Raw photo editing with an assistant that shows its working.</p>

      <button type="button" className="btn ember" onClick={() => inputRef.current?.click()}>
        Open photos
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []).filter(
            (file) => classifySource(file.name) !== null,
          );
          if (files.length > 0) addFiles(files);
          else if (event.target.files && event.target.files.length > 0) {
            notify('Lumos cannot read those files', 'error');
          }
          event.target.value = '';
        }}
      />

      <div className="label" aria-live="polite">
        {over ? 'Release to open' : 'or drop files anywhere'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <Tip title="Files Lumos can open" body={FORMATS}>
          <button
            type="button"
            className="link-btn"
            aria-expanded={showFormats}
            aria-controls="supported-formats"
            onClick={() => setShowFormats((open) => !open)}
          >
            Supported formats
          </button>
        </Tip>

        {showFormats ? (
          <div
            className="scope-readout"
            id="supported-formats"
            style={{ display: 'block', maxWidth: 520, textAlign: 'left', lineHeight: 1.7 }}
          >
            {FORMATS}
          </div>
        ) : null}
      </div>
    </div>
  );
}
